import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analyticsService, AnalyticsEvent, AnalyticsProperties } from '../services/analytics';

// Hook for automatic page tracking
export const usePageTracking = () => {
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();

  useEffect(() => {
    // Get page name from pathname
    const pageName = location.pathname === '/' ? 'Home' : 
                    location.pathname.split('/').filter(Boolean).join(' > ');
    
    // Track page view with user context
    analyticsService.trackPageView(pageName, {
      user_plan: userProfile?.plan || 'anonymous',
      is_authenticated: !!currentUser
    });
  }, [location, currentUser, userProfile]);
};

// Hook for tracking user session analytics
export const useUserAnalytics = () => {
  const { currentUser, userProfile } = useAuth();
  const isUserSet = useRef(false);

  useEffect(() => {
    if (currentUser && userProfile && !isUserSet.current) {
      // Set user for analytics
      analyticsService.setUser(currentUser.uid, {
        plan: userProfile.plan,
        videos_processed: userProfile.videosProcessed.toString(),
        created_at: userProfile.createdAt?.toISOString() || '',
        has_subscription: userProfile.hasActiveSubscription ? 'true' : 'false'
      });
      isUserSet.current = true;
    } else if (!currentUser && isUserSet.current) {
      // Reset when user logs out
      isUserSet.current = false;
    }
  }, [currentUser, userProfile]);
};

// Hook for tracking time spent and abandonment
export const useTimeTracking = (eventPrefix: string) => {
  const startTimeRef = useRef<number>(Date.now());
  const isActiveRef = useRef<boolean>(true);

  useEffect(() => {
    startTimeRef.current = Date.now();
    isActiveRef.current = true;
    
    return () => {
      isActiveRef.current = false;
    };
  }, []);

  const trackCompletion = (additionalProps?: AnalyticsProperties) => {
    if (isActiveRef.current) {
      const timeSpent = Date.now() - startTimeRef.current;
      analyticsService.trackEvent(`${eventPrefix}_completed`, {
        time_spent_seconds: Math.round(timeSpent / 1000),
        ...additionalProps
      });
    }
  };

  const trackAbandonment = (step: string, additionalProps?: AnalyticsProperties) => {
    if (isActiveRef.current) {
      const timeSpent = Date.now() - startTimeRef.current;
      analyticsService.trackEvent(`${eventPrefix}_abandoned`, {
        abandonment_step: step,
        time_spent_seconds: Math.round(timeSpent / 1000),
        ...additionalProps
      });
    }
  };

  const getTimeSpent = () => {
    return Date.now() - startTimeRef.current;
  };

  return { trackCompletion, trackAbandonment, getTimeSpent };
};

// Hook for tracking upload funnel
export const useUploadTracking = () => {
  const uploadStartTime = useRef<number>(0);
  const processingStartTime = useRef<number>(0);

  const trackUploadStarted = (fileName: string, fileSize: number) => {
    uploadStartTime.current = Date.now();
    analyticsService.trackVideoUploadStarted(fileName, fileSize);
  };

  const trackUploadCompleted = (fileName: string) => {
    const uploadTime = Date.now() - uploadStartTime.current;
    analyticsService.trackVideoUploadCompleted(fileName, uploadTime);
  };

  const trackUploadFailed = (fileName: string, error: string) => {
    const uploadTime = Date.now() - uploadStartTime.current;
    analyticsService.trackVideoUploadFailed(fileName, error, uploadTime);
  };

  const trackProcessingStarted = (videoId: string, duration?: number) => {
    processingStartTime.current = Date.now();
    analyticsService.trackVideoProcessingStarted(videoId, duration);
  };

  const trackProcessingCompleted = (videoId: string) => {
    const processingTime = Date.now() - processingStartTime.current;
    analyticsService.trackVideoProcessingCompleted(videoId, processingTime);
  };

  const trackProcessingFailed = (videoId: string, error: string) => {
    const processingTime = Date.now() - processingStartTime.current;
    analyticsService.trackVideoProcessingFailed(videoId, error, processingTime);
  };

  return {
    trackUploadStarted,
    trackUploadCompleted, 
    trackUploadFailed,
    trackProcessingStarted,
    trackProcessingCompleted,
    trackProcessingFailed
  };
};

// Hook for tracking subscription funnel
export const useSubscriptionTracking = () => {
  const { userProfile } = useAuth();
  
  const trackModalOpened = (trigger: string) => {
    analyticsService.trackSubscriptionModalOpened(trigger, userProfile?.plan || 'free');
  };

  const trackPlanViewed = (planName: string, planPrice: number) => {
    analyticsService.trackSubscriptionPlanViewed(planName, planPrice);
  };

  const trackUpgradeStarted = (planName: string, planPrice: number) => {
    analyticsService.trackSubscriptionStarted(planName, planPrice);
  };

  const trackUpgradeCompleted = (planName: string, planPrice: number, subscriptionId: string) => {
    analyticsService.trackSubscriptionCompleted(planName, planPrice, subscriptionId);
  };

  const trackCheckoutAbandoned = (planName: string, step: string) => {
    analyticsService.trackCheckoutAbandoned(planName, step);
  };

  return {
    trackModalOpened,
    trackPlanViewed,
    trackUpgradeStarted,
    trackUpgradeCompleted,
    trackCheckoutAbandoned
  };
};

// Hook for tracking errors
export const useErrorTracking = () => {
  const trackError = (errorType: AnalyticsEvent, errorMessage: string, context?: string) => {
    analyticsService.trackError(errorType, errorMessage, context);
  };

  const trackApiError = (endpoint: string, statusCode: number, errorMessage: string) => {
    analyticsService.trackError(AnalyticsEvent.API_ERROR, errorMessage, `${endpoint} (${statusCode})`);
  };

  const trackAuthError = (method: string, errorMessage: string) => {
    analyticsService.trackError(AnalyticsEvent.AUTHENTICATION_ERROR, errorMessage, method);
  };

  const trackPaymentError = (planName: string, errorMessage: string) => {
    analyticsService.trackError(AnalyticsEvent.PAYMENT_ERROR, errorMessage, planName);
  };

  return {
    trackError,
    trackApiError,
    trackAuthError,
    trackPaymentError
  };
};

// Main analytics hook that combines common functionality
export const useAnalytics = () => {
  const { trackError, trackApiError, trackAuthError, trackPaymentError } = useErrorTracking();
  const { trackModalOpened, trackPlanViewed, trackUpgradeStarted, trackUpgradeCompleted, trackCheckoutAbandoned } = useSubscriptionTracking();
  const uploadTracking = useUploadTracking();

  return {
    // Direct analytics service access
    track: analyticsService.trackEvent.bind(analyticsService),
    
    // Authentication tracking
    trackSignupStarted: analyticsService.trackSignupStarted.bind(analyticsService),
    trackSignupCompleted: analyticsService.trackSignupCompleted.bind(analyticsService),
    trackSignupAbandoned: analyticsService.trackSignupAbandoned.bind(analyticsService),
    trackLoginStarted: analyticsService.trackLoginStarted.bind(analyticsService),
    trackLoginCompleted: analyticsService.trackLoginCompleted.bind(analyticsService),
    
    // Upload funnel
    ...uploadTracking,
    
    // Subscription funnel
    trackSubscriptionModalOpened: trackModalOpened,
    trackSubscriptionPlanViewed: trackPlanViewed,
    trackSubscriptionStarted: trackUpgradeStarted,
    trackSubscriptionCompleted: trackUpgradeCompleted,
    trackSubscriptionCheckoutAbandoned: trackCheckoutAbandoned,
    
    // Limits and conversions
    trackFreeLimitReached: analyticsService.trackFreeLimitReached.bind(analyticsService),
    trackUpgradePromptShown: analyticsService.trackUpgradePromptShown.bind(analyticsService),
    trackUpgradePromptClicked: analyticsService.trackUpgradePromptClicked.bind(analyticsService),
    
    // Feature usage
    trackGuestModeUsed: analyticsService.trackGuestModeUsed.bind(analyticsService),
    trackUrlImportUsed: analyticsService.trackUrlImportUsed.bind(analyticsService),
    trackFeedbackSubmitted: analyticsService.trackFeedbackSubmitted.bind(analyticsService),
    trackClipDownloaded: analyticsService.trackClipDownloaded.bind(analyticsService),
    
    // Error tracking
    trackError,
    trackApiError,
    trackAuthError,
    trackPaymentError
  };
};
